import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Add as AddIcon, AutoAwesome as AutoAwesomeIcon, Delete as DeleteIcon, Edit as EditIcon, Flag as FlagIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  createTheme,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tutorials } from "../config/tutorials";
import { usePageTutorial } from "../hooks/useTutorial";
import { AgentMessagingService } from "../services/AgentMessagingService";
import { getCurrentUser } from "../services/firebase";
import { priorityService } from "../services/PriorityService";
import { CreatePriorityData, Priority, UpdatePriorityData } from "../types";
import { DateUtils } from "../utils/dateUtils";
import { HelpIcon } from "./HelpIcon";

const categories = [
  { value: "Team", label: "Team", color: "#1976d2" },
  { value: "Business", label: "Business", color: "#388e3c" },
  { value: "Personal", label: "Personal", color: "#f57c00" },
];

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
  },
});

interface SortableItemProps {
  priority: Priority;
  onItemClick: (priority: Priority) => void;
  onDeleteItem: (priorityId: string) => void;
  onAIAssist: (priority: Priority) => void;
  formatDate: (date: Date) => string;
  aiLoading: string | null;
  deleting: string | null;
}

const SortableItem: React.FC<SortableItemProps> = ({ priority, onItemClick, onDeleteItem, onAIAssist, formatDate, aiLoading, deleting }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: priority.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getCategoryColor = (category: string) => {
    const cat = categories.find((c) => c.value === category);
    return cat ? cat.color : "#666";
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        border: `1px solid ${getCategoryColor(priority.category)}`,
        borderRadius: 2,
        mb: 1,
        bgcolor: "white",
        cursor: "grab",
        listStyle: "none",
        "&:hover": {
          bgcolor: "#f5f5f5",
        },
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" component="span">
              {priority.title}
            </Typography>
            <Chip
              label={priority.category}
              size="small"
              sx={{
                bgcolor: getCategoryColor(priority.category),
                color: "white",
                fontSize: "0.75rem",
              }}
            />
          </Box>
        }
        secondary={
          <Box>
            {priority.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {priority.description}
              </Typography>
            )}
          </Box>
        }
      />
      <ListItemSecondaryAction>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => onAIAssist(priority)} disabled={aiLoading === priority.id} sx={{ color: "primary.main" }} title="Review this priority with AI">
            {aiLoading === priority.id ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
          </IconButton>
          <IconButton size="small" onClick={() => onItemClick(priority)}>
            <EditIcon />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDeleteItem(priority.id)} disabled={deleting === priority.id}>
            {deleting === priority.id ? <CircularProgress size={16} /> : <DeleteIcon />}
          </IconButton>
        </Box>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export const PrioritiesPage: React.FC = () => {
  const navigate = useNavigate();
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPriority, setSelectedPriority] = useState<Priority | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newPriority, setNewPriority] = useState<CreatePriorityData>({
    title: "",
    description: "",
    category: "Personal",
  });
  const [aiLoading, setAiLoading] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const user = getCurrentUser();

  // Initialize tutorial for this page
  usePageTutorial("priorities_page_tour", tutorials.priorities);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (user) {
      loadPriorities();
    }
  }, [user]);

  const loadPriorities = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const priorityList = await priorityService.getPriorities();
      setPriorities(priorityList);
    } catch (err) {
      setError("Failed to load priorities");
      console.error("Error loading priorities:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const activePriority = priorities.find((p) => p.id === active.id);
    const overPriority = priorities.find((p) => p.id === over.id);

    if (!activePriority || !overPriority) {
      return;
    }

    const activeIndex = priorities.findIndex((p) => p.id === active.id);
    const overIndex = priorities.findIndex((p) => p.id === over.id);

    if (activeIndex === -1 || overIndex === -1) {
      return;
    }

    // Optimistic update: immediately reorder the priorities in the UI
    const newPriorities = [...priorities];
    const [movedPriority] = newPriorities.splice(activeIndex, 1);
    newPriorities.splice(overIndex, 0, movedPriority);

    // Update the order values to match the new positions
    const updatedPriorities = newPriorities.map((priority, index) => ({
      ...priority,
      order: index + 1,
    }));

    setPriorities(updatedPriorities);
    setReordering(true);

    try {
      // Call the API to update the order
      const result = await priorityService.changePriorityOrder(activePriority.id, overIndex + 1);

      // Update with the server response (which includes the full list)
      setPriorities(result.priorities);
    } catch (err) {
      // Revert to original order on error
      setPriorities(priorities);
      setError("Failed to update priority order");
      console.error("Error updating priority order:", err);
    } finally {
      setReordering(false);
    }
  };

  const handleItemClick = (priority: Priority) => {
    setSelectedPriority(priority);
    setNewPriority({
      title: priority.title,
      description: priority.description || "",
      category: priority.category,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPriority(null);
    setNewPriority({
      title: "",
      description: "",
      category: "Personal",
    });
  };

  const handleDeleteItem = async (priorityId: string) => {
    if (!user) return;

    setDeleting(priorityId);
    try {
      await priorityService.deletePriority(priorityId);
      await loadPriorities();
    } catch (err) {
      setError("Failed to delete priority");
      console.error("Error deleting priority:", err);
    } finally {
      setDeleting(null);
    }
  };

  const handleAIAssist = async (priority: Priority) => {
    try {
      setAiLoading(priority.id);

      // Find the PersonalAssistant agent
      const agentChatInfos = await AgentMessagingService.getAgentChatInfo();
      const personalAssistantAgent = agentChatInfos.find((a) => a.agentType === "PersonalAssistantAgent");

      if (!personalAssistantAgent) {
        throw new Error("PersonalAssistant agent not found");
      }

      const createdDate = DateUtils.formatForMessage(priority.createdAt);
      const message = `Please help me review this priority: "${priority.title}"${priority.priorityName ? ` (also known as: ${priority.priorityName})` : ""} (${priority.category}, order: ${priority.order}). ${priority.description ? `Description: ${priority.description}` : ""} Created: ${createdDate}.`;

      // Fire and forget API call
      AgentMessagingService.sendUserMessageToAgent(personalAssistantAgent.agentId, message).catch((error) => {
        console.error("Error sending AI assist message:", error);
      });

      // Navigate to chat with PersonalAssistant
      navigate(`/chat/${personalAssistantAgent.agentId}`);
    } catch (error) {
      console.error("Error initiating AI assist:", error);
      setError("Failed to start AI assistance");
    } finally {
      setAiLoading(null);
    }
  };

  const handleAddItem = async () => {
    if (!user || !newPriority.title.trim()) return;

    setSaving(true);
    try {
      await priorityService.createPriority(newPriority);
      setNewPriority({
        title: "",
        description: "",
        category: "Personal",
      });
      setOpenDialog(false);
      await loadPriorities();
    } catch (err) {
      setError("Failed to create priority");
      console.error("Error creating priority:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateItem = async () => {
    if (!user || !selectedPriority || !newPriority.title.trim()) return;

    setSaving(true);
    try {
      const updateData: UpdatePriorityData = {
        title: newPriority.title,
        description: newPriority.description,
        category: newPriority.category,
      };

      await priorityService.updatePriority(selectedPriority.id, updateData);
      setNewPriority({
        title: "",
        description: "",
        category: "Personal",
      });
      setSelectedPriority(null);
      setOpenDialog(false);
      await loadPriorities();
    } catch (err) {
      setError("Failed to update priority");
      console.error("Error updating priority:", err);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date: Date | any) => {
    return DateUtils.formatForDisplay(date);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FlagIcon />
            Priorities
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HelpIcon
              tutorialConfig={tutorials.priorities}
              pageDescription="Set and manage your most important goals and priorities. Organize them by category, drag to reorder, and stay focused on what matters most to you!"
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)} data-tutorial="add-priority">
              Add Priority
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ position: "relative" }}>
            {reordering && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255, 255, 255, 0.8)",
                  zIndex: 1000,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Reordering priorities...</Typography>
                </Box>
              </Box>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    All Priorities ({priorities.length})
                  </Typography>

                  {priorities.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                      No priorities yet. Add your first priority!
                    </Typography>
                  ) : (
                    <Box sx={{ listStyle: "none", p: 0, m: 0 }} data-tutorial="priorities-list">
                      <SortableContext items={priorities.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                        {priorities.map((priority) => (
                          <SortableItem
                            key={priority.id}
                            priority={priority}
                            onItemClick={handleItemClick}
                            onDeleteItem={handleDeleteItem}
                            onAIAssist={handleAIAssist}
                            formatDate={formatDate}
                            aiLoading={aiLoading}
                            deleting={deleting}
                          />
                        ))}
                      </SortableContext>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </DndContext>
          </Box>
        )}

        {/* Add/Edit Priority Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedPriority ? "Edit Priority" : "Add New Priority"}</DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 2,
                mt: 1,
              }}
            >
              <Box sx={{ gridColumn: "1 / -1" }}>
                <TextField fullWidth label="Priority Title" value={newPriority.title} onChange={(e) => setNewPriority({ ...newPriority, title: e.target.value })} variant="outlined" required />
              </Box>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  value={newPriority.description}
                  onChange={(e) => setNewPriority({ ...newPriority, description: e.target.value })}
                  variant="outlined"
                  multiline
                  rows={3}
                />
              </Box>
              <Box>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select value={newPriority.category} onChange={(e) => setNewPriority({ ...newPriority, category: e.target.value as "Team" | "Business" | "Personal" })} label="Category">
                    {categories.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={selectedPriority ? handleUpdateItem : handleAddItem}
              disabled={!newPriority.title.trim() || saving}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              {saving ? "Saving..." : selectedPriority ? "Update Priority" : "Add Priority"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};
